import {Component, ElementRef, Inject, OnInit, Optional, ViewChild} from '@angular/core';
import {FormControl, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Prize} from '../../../../core/model/resources/race';
import {MessageDialogComponent} from '../../message-dialog/message-dialog.component';
import {v4} from 'uuid';
import {S3Service} from '../../../../core/services/s3.service';

@Component({
    selector: 'app-prize-dialog',
    templateUrl: './prize-dialog.component.html',
    styleUrls: ['./prize-dialog.component.scss'],
    standalone: false
})
export class PrizeDialogComponent implements OnInit {
  titleControl?: FormControl<string>;
  textControl?: FormControl<string>;
  imageUrl?: string;
  isEdit?: boolean;
  editIdx?: number;
  defaultTitle = '';
  defaultText = '';

  @ViewChild('imageFile') public imageFile?: ElementRef;

  static async open(dialog: MatDialog, prizes: Prize[], editIdx?: number): Promise<Prize | null> {
    return await dialog.open(PrizeDialogComponent, {
      width: '60vw',
      data: {
        prizes,
        editIdx
      }
    }).afterClosed().toPromise();
  }

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) private data: { prizes: Prize[], editIdx?: number },
    private s3Service: S3Service,
    private dialogRef: MatDialogRef<PrizeDialogComponent>,
    private dialog: MatDialog,
  ) {
  }

  ngOnInit(): void {
    this.editIdx = this.data.editIdx;
    this.isEdit = (this.editIdx !== undefined);

    if (this.isEdit) {
      this.defaultTitle = this.data.prizes[this.editIdx!].title;
      this.defaultText = this.data.prizes[this.editIdx!].text;
      this.imageUrl = this.data.prizes[this.editIdx!].imageUrl;
    }

    this.titleControl = new FormControl<string>(this.defaultTitle, {
      validators: [Validators.required, Validators.maxLength(20)],
      nonNullable: true,
    });
    this.textControl = new FormControl<string>(this.defaultText, {
      validators: [Validators.required, Validators.maxLength(400)],
      nonNullable: true,
    });
  }

  async onImageChanged(): Promise<void> {
    const file: File = this.imageFile!.nativeElement.files[0];
    const id = v4().toString();
    const imageSize = await this.s3Service.getImageSize(file);
    if (imageSize.width < 650) {
      MessageDialogComponent.createErrorDialog(this.dialog, '画像の幅は650px以上にしてください。');
      return;
    }
    if (1024 * 1024 * 5 < file.size) {
      MessageDialogComponent.createErrorDialog(this.dialog, '画像は5MB以下にしてください。');
      return;
    }
    if (this.imageUrl) {
      const isOK = await MessageDialogComponent.createPositiveConfirmDialog(this.dialog, '画像を入れ替えます。この操作は即時反映されます。よろしいですか？');
      if (!isOK) {
        return;
      }
    }

    try {
      await this.s3Service.putStoreImage(id, file);
      if (this.imageUrl) {
        await this.s3Service.deleteStoreImageFromUrl(this.imageUrl);
      }
      this.imageUrl = this.s3Service.getStoreImageUrl(id);
    } catch (e: any) {
      MessageDialogComponent.createErrorDialog(this.dialog, '画像アップロードに失敗しました。再度選択してください。');
    }
  }

  async onDeleteImageClicked(): Promise<void> {
    const isOK = await MessageDialogComponent.createPositiveConfirmDialog(this.dialog, '画像を削除します。この操作は即時反映されます。よろしいですか？');
    if (isOK) {
      if (this.imageUrl) {
        await this.s3Service.deleteStoreImageFromUrl(this.imageUrl);
      }
      this.imageUrl = undefined;
    }
  }

  onSave(): void {
    if (!this.validate()) {
      return;
    }

    const prize: Prize = {
      title: this.titleControl!.value,
      text: this.textControl!.value
    };
    if (this.imageUrl) {
      prize.imageUrl = this.imageUrl;
    }
    this.dialogRef.close(prize);
  }

  validate(): boolean {
    if (this.titleControl!.hasError('required')) {
      MessageDialogComponent.createErrorDialog(this.dialog, 'タイトルを入力してください。');
      return false;
    }
    if (this.titleControl!.hasError('maxLength')) {
      MessageDialogComponent.createErrorDialog(this.dialog, 'タイトルは20文字以内で入力してください。');
      return false;
    }
    const sameName = this.data.prizes.find((item, idx) => {
      // 登録済みの内容を編集する際にタイトルは変更しない場合は重複の登録判定から除外
      if (this.isEdit && idx === this.editIdx) {
        return false;
      }
      return item.title === this.titleControl!.value;
    });
    if (sameName) {
      MessageDialogComponent.createErrorDialog(this.dialog, '既に登録済みのタイトルです。');
      return false;
    }
    if (this.textControl!.hasError('required')) {
      MessageDialogComponent.createErrorDialog(this.dialog, '本文を入力してください。');
      return false;
    }
    if (this.textControl!.hasError('maxLength')) {
      MessageDialogComponent.createErrorDialog(this.dialog, '本文は400文字以内で入力してください。');
      return false;
    }

    return true;
  }
}
